import React, { useMemo, useRef, useState } from "react";
import useForm from "./index";
import Label from "./label";
import { TForm, TInputElementProps, TInputProps } from "@/components/form/types";
import ChevronSVG from "@/assets/icons-js/chevron";
import useOnClickOutside from "@/hooks/OnClickOutside";

export const SelectInput = ({ container, label, tooltip, input, className, options, hidden }: TInputElementProps<TSelectInput>) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLInputElement>(null);

    const select = (e: React.SyntheticEvent<HTMLDivElement>) => {
        if (!ref?.current) return;

        const newValue = e.currentTarget.getAttribute("data-value") ?? "";

        // Create a new input event
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
        if (nativeInputValueSetter) {
            nativeInputValueSetter.call(ref.current, newValue);
        }

        setOpen(false);
        const event = new Event("input", { bubbles: true });
        ref.current.dispatchEvent(event);
    }

    const click = (e: React.SyntheticEvent<HTMLDivElement>) => {
        e.preventDefault();
        setOpen(!open);
        if (document.activeElement !== ref.current) ref.current?.focus();
    }

    const mouseDown = (e: React.SyntheticEvent<HTMLDivElement>) => {
        e.preventDefault();
    }

    useOnClickOutside(containerRef, () => {
        setOpen(false)
    });

    const value = useMemo(() => {
        if ((input.value?.length ?? 0) === 0 && options?.includeBlank) return options?.includeBlank;

        const _value = options?.data?.find(item => item.value === input.value);
        if (_value) return _value.label;

        return input.value;
    }, [input.value])

    if (hidden) {
        return null;
    }

    return (
        <div {...container} className={container?.className ?? className} style={container?.style} ref={containerRef}>
            <Label {...label} tooltip={tooltip} options={options}/>
            <div className="select" data-open={open}>
                <div className="select__header" onMouseDown={mouseDown}>
                    <div onClick={click} onMouseDown={mouseDown} style={{ pointerEvents: input.disabled ? "none" : "auto" }}>
                        <ChevronSVG/>
                    </div>
                    <div onClick={click} onMouseDown={mouseDown} style={{ pointerEvents: input.disabled ? "none" : "auto" }}/>
                    <input {...input} value={value} ref={ref} readOnly/>
                </div>
                <div className="options">
                    <div>
                        { options?.includeBlank && <div data-value="" onClick={select}>{options.includeBlank}</div> }
                        { options?.data?.map?.(item => {
                            return (
                                <div key={item?.value} data-value={item?.value} onClick={select}>
                                    {item?.label}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

type TSelectInput = string | undefined;
const useSelectInput = <T extends TSelectInput>(props: TInputProps<T>): TForm<TSelectInput> => {
    const form = useForm<TSelectInput>({
        id: "select",
        default: props?.options?.includeBlank ? undefined : props?.options?.data?.[0]?.value ?? "",
        ...props,
    })

    return {
        ...form,
    }
}

export default useSelectInput;
